import { getAllOps } from '@/shared/lib/op-registry'
import type { OpDefinition, OpMenuMeta } from '@/shared/lib/op-registry'
import type { NodeMoreOpItem, ResolveRegisteredOpMenuItemsArgs } from './types'

// 文件说明：把 OP 注册表转换成当前节点 toolbar 可展示的更多操作菜单。

function matchesMenu({
    menu,
    sourceNodeType,
    sourceToolbar,
}: {
    menu: OpMenuMeta
    sourceNodeType: string
    sourceToolbar?: string
}) {
    if (menu.sourceNodeTypes && !menu.sourceNodeTypes.includes(sourceNodeType)) {
        return false
    }

    if (menu.sourceToolbars) {
        // 特殊处理：如果是仅用于预览裁剪的专用菜单，必须严格匹配 toolbar
        if (menu.sourceToolbars.includes('previewCrop')) {
            if (sourceToolbar !== 'previewCrop') {
                return false
            }
        } else {
            // 常规情况：如果传入了 sourceToolbar 才进行校验，没传则放行
            if (sourceToolbar && !menu.sourceToolbars.includes(sourceToolbar)) {
                return false
            }
        }
    }

    return true
}

// 判断 OP 是否接受当前来源节点类型。
function canAcceptSourceType(definition: OpDefinition, sourceNodeType: string, targetNodeType: string) {
    const accepts = typeof definition.acceptSourceTypes === 'function'
        ? definition.acceptSourceTypes(targetNodeType)
        : definition.acceptSourceTypes

    return !accepts?.length || accepts.includes(sourceNodeType)
}

// 解析当前节点可用的已注册 OP 菜单项。
export function resolveRegisteredOpMenuItems({
    sourceNodeType,
    sourceToolbar,
}: ResolveRegisteredOpMenuItemsArgs): NodeMoreOpItem[] {
    return getAllOps()
        .flatMap((definition) => {
            if (!definition.menu) {
                return []
            }

            const menus = Array.isArray(definition.menu) ? definition.menu : [definition.menu]

            return menus.flatMap((menu) => {
                const isListInput = sourceNodeType === 'gallery' || sourceNodeType === 'text-list' || sourceNodeType === 'audio-list' || sourceNodeType === 'video-list'
                // For a single menu resolution (from an edge or single node), isMultipleInputs is always false.
                const effectiveTargetNodeType = resolveActualTargetNodeType(
                    definition.op,
                    menu.create.nodeType,
                    isListInput,
                    false
                )

                if (!canAcceptSourceType(definition, sourceNodeType, effectiveTargetNodeType)) {
                    return []
                }

                // 若 OP 声明了它所支持的输出类型，但转换后的类型不在支持列表中，则排除
                if (definition.targetNodeTypes && !definition.targetNodeTypes.includes(effectiveTargetNodeType)) {
                    return []
                }

                if (!matchesMenu({ menu, sourceNodeType, sourceToolbar })) {
                    return []
                }

                const Icon = menu.icon
                const label = menu.label ?? definition.label
                const order = menu.order ?? Number.MAX_SAFE_INTEGER

                return [{
                    item: {
                        nodeType: effectiveTargetNodeType,
                        op: definition.op,
                        icon: Icon
                            ? <Icon className="h-4 w-4" />
                            : <span className="text-xs font-medium">{label[0]?.toUpperCase()}</span>,
                        iconComponent: Icon,
                        label,
                        toolbar: resolveTargetToolbar(effectiveTargetNodeType, menu.create.toolbar),
                        autoOpenEditor: menu.create.autoOpenEditor,
                        targetNodeTypes: definition.targetNodeTypes,
                        createCustomData: menu.create.createCustomData,
                    },
                    order,
                }]
            })
        })
        .sort((left, right) => left.order - right.order || left.item.label.localeCompare(right.item.label, 'zh-CN'))
        .map((entry) => entry.item)
}

export const GENERATE_OPS = new Set([
    'text2image',
    'text2video',
    'text2audio',
    'text-llm',
])

export function resolveActualTargetNodeType(
    opId: string,
    baseTargetNodeType: string,
    isListInput: boolean,
    isMultipleInputs: boolean
): string {
    // 强制单节点输出的特例：拼图、裁剪、视频合成
    if (opId === 'collage') return 'image'
    if (opId === 'crop') return 'image'
    if (opId === 'video-compose') return 'video'

    // 强制列表节点输出的特例：宫格切分、添加到列表
    if (opId === 'grid-split') return 'gallery'
    if (opId === 'add2list-image') return 'gallery'
    if (opId === 'add2list-text') return 'text-list'

    const isGenerateOp = GENERATE_OPS.has(opId)

    if (isGenerateOp) {
        // 规则 3：生成类操作
        // - 若上游包含至少一个列表节点 -> 列表节点
        // - 若上游均为普通节点（无论是单个还是多个） -> 普通操作节点
        if (isListInput) {
            if (baseTargetNodeType === 'image') return 'gallery'
            if (baseTargetNodeType === 'text') return 'text-list'
            if (baseTargetNodeType === 'audio') return 'audio-list'
            if (baseTargetNodeType === 'video') return 'video-list'
        }
        return baseTargetNodeType
    } else {
        // 规则 4：非生成类操作
        // - 对多个普通节点，或对单个/多个列表节点进行操作时 -> 列表节点
        if (isListInput || isMultipleInputs) {
            if (baseTargetNodeType === 'image') return 'gallery'
            if (baseTargetNodeType === 'text') return 'text-list'
            if (baseTargetNodeType === 'audio') return 'audio-list'
            if (baseTargetNodeType === 'video') return 'video-list'
        }
        return baseTargetNodeType
    }
}

export function resolveTargetToolbar(targetNodeType: string, baseToolbar?: string): string | undefined {
    if (targetNodeType === 'gallery') return 'galleryEditor'
    if (targetNodeType === 'text-list') return 'textListEditor'
    if (targetNodeType === 'audio-list') return 'audioListEditor'
    if (targetNodeType === 'video-list') return 'videoListEditor'
    return baseToolbar
}
